/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSortDown } from "react-icons/fa";

export interface Column {
  header: string;
  key: string;
  render?: (row: any, index: number) => React.ReactNode;
}

interface MediaPreparationTableProps {
  columns: Column[];
  data: any[];
  loading?: boolean;
  currentPage: number;
  pageSize: number;
  renderAction?: (row: any) => React.ReactNode;
  emptyMessage?: string;
  onSortClick?: (key: string, value: boolean) => void;
}

const MediaPreparationTable: React.FC<MediaPreparationTableProps> = ({
  columns,
  data,
  currentPage,
  loading,
  pageSize,
  renderAction,
  emptyMessage = "No data available",
  onSortClick

}) => {
  const { t } = useTranslation();
  const [sortValue, setSortValue] = React.useState(true);
    const handleSortClick = (key: string) => {
        if (onSortClick) {
            onSortClick(key, sortValue);
            setSortValue(!sortValue);
        }
    };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">

        <div className="overflow-x-auto overflow-y-auto max-h-[320px]">
        <table className="w-full text-left border-collapse">

            {/* TABLE HEADER */}
            <thead className="bg-[rgb(54,64,93)] text-white sticky top-0 z-20">
            <tr className="text-[10px] font-semibold uppercase tracking-[0.1em]">
                {columns.map((col, index) => (
                <th
                    key={index}
                    className={`py-3 bg-[rgb(54,64,93)] whitespace-nowrap ${
                    col.key === "slno" ? "w-[40px] px-2" : "px-6"
                    }`}
                >
                <div
                    className="flex items-center gap-1 cursor-pointer"
                    onClick={() => handleSortClick(col.key)}
                >
                    {t(col.header)}

                    {col.key !== "slno" &&
                    col.key !== "storage" &&
                    col.key !== "stock" &&
                    col.key !== "initialPh" &&
                    col.key !== "finalPh" &&
                    col.key !== "shelfLife" &&
                    col.key !== "select" &&
                    col.key !== "status" &&
                    col.key !== "system_stock_display" &&
                    col.key !== "last_reconciled_at" &&
                    col.key !== "batch_status" &&
                    col.key !== "reconciliation_status" &&
                    col.key !== "verified_at" &&
                    col.key !== "reconciliation_status" && (
                    <FaSortDown size={12} />
                    )}
                </div>
                </th>
                ))}
                {renderAction && (
                <th className="w-[30px] px-2 py-3 bg-[rgb(54,64,93)] text-left">{t('mediaPowder.lotDetails.lotTable.action')}</th>
                )}
            </tr>
            </thead>

            {/* TABLE BODY */}
            <tbody className="divide-y divide-gray-100">
            {loading ? (
                <tr>
                    <td colSpan={columns.length + (renderAction ? 1 : 0)}>
                    <div className="loader-container">
                        <div className="spinner"></div>
                        <p className="loading-text">Loading... Please wait</p>
                    </div>
                    </td>
                </tr>
                ) : data.length > 0 ? (
                data.map((row, i) => (
                <tr
                    key={i}
                    className="hover:bg-slate-50/50 transition-colors group"
                >
                    {columns.map((col, idx) => (
                    <React.Fragment key={idx}>
                        {col.key === "slno" ? (
                            <td className="w-[40px] px-2 py-1 text-sm font-medium text-slate-400 whitespace-nowrap">
                                {(currentPage - 1) * pageSize + i + 1}
                            </td>
                            ) : col.render ? (
                        col.render(row, i) // FULL TD comes from render
                        ) : (
                        <td className="px-4 py-1 text-sm whitespace-nowrap">
                            {row[col.key]}
                        </td>
                        )}
                    </React.Fragment>
                    ))}

                    {renderAction && (
                    <td className="w-[30px] px-2 py-1 text-center">
                        {renderAction(row)}
                    </td>
                    )}
                </tr>
                ))
            ) : (
                <tr>
                <td colSpan={columns.length + 1} className="px-8 py-16 text-center">
                <div className="flex flex-col items-center gap-3 text-slate-400">
                    <span className="material-symbols-outlined text-5xl">{t('mediaPowder.icons.inventory2')}</span>
                    <p className="text-sm font-bold tracking-tight">
                    {emptyMessage}
                    </p>
                </div>
                </td>
                </tr>
            )}
            </tbody>

        </table>
        </div>

    </div>
    );
};

export default MediaPreparationTable;